import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

export interface CopilotMessage {
  text: React.ReactNode;
  action?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
  /** "I'll do it later" / "Got it" — dismisses or advances queue */
  laterLabel?: string;
  /** Called when the user dismisses this message */
  onDismiss?: () => void;
  /** Replaces window.confirm — shows Yes/Cancel buttons */
  confirm?: { label: string; onConfirm: () => void };
}

interface CopilotContextValue {
  message: CopilotMessage | null;
  open: boolean;
  /** Whether the ! badge is visible on the icon */
  badgeActive: boolean;
  showMessage: (msg: CopilotMessage) => void;
  /** Set message + badge but don't open bubble — for re-visits after dismissal */
  showMessageSilent: (msg: CopilotMessage) => void;
  /** Queue multiple messages — first shows immediately, rest advance on acknowledge */
  queueMessages: (msgs: CopilotMessage[]) => void;
  clearMessage: () => void;
  openBubble: () => void;
  closeBubble: () => void;
  /** Advance queue or (if last) close bubble + hide badge. Used by laterLabel / "Got it". */
  acknowledgeMessage: () => void;
  /** Hard dismiss: clear queue, close bubble, hide badge. Used by X button. */
  dismissForNow: () => void;
  /** Call when confirm action is taken — advances queue */
  advanceQueue: () => void;
}

const CopilotContext = createContext<CopilotContextValue | null>(null);

export const useCopilot = (): CopilotContextValue => {
  const ctx = useContext(CopilotContext);
  if (!ctx) throw new Error('useCopilot must be used inside CopilotProvider');
  return ctx;
};

export const CopilotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [message, setMessage] = useState<CopilotMessage | null>(null);
  const [open, setOpen] = useState(false);
  const [badgeActive, setBadgeActive] = useState(false);
  const queueRef = useRef<CopilotMessage[]>([]);

  const showMessage = useCallback((msg: CopilotMessage) => {
    queueRef.current = [];
    setMessage(msg);
    setOpen(true);
    setBadgeActive(true);
  }, []);

  const showMessageSilent = useCallback((msg: CopilotMessage) => {
    queueRef.current = [];
    setMessage(msg);
    setOpen(false);
    setBadgeActive(true);
  }, []);

  const queueMessages = useCallback((msgs: CopilotMessage[]) => {
    if (msgs.length === 0) return;
    const [first, ...rest] = msgs;
    queueRef.current = rest;
    setMessage(first);
    setOpen(true);
    setBadgeActive(true);
  }, []);

  const advanceQueue = useCallback(() => {
    const next = queueRef.current[0];
    if (next) {
      queueRef.current = queueRef.current.slice(1);
      setMessage(next);
      setOpen(true);
    } else {
      setMessage(null);
      setOpen(false);
      setBadgeActive(false);
    }
  }, []);

  // "Got it" / laterLabel: advance to next queued message, or close+hide badge if last
  const acknowledgeMessage = useCallback(() => {
    if (queueRef.current.length > 0) {
      const next = queueRef.current[0];
      queueRef.current = queueRef.current.slice(1);
      setMessage(prev => { prev?.onDismiss?.(); return next; });
      setOpen(true);
    } else {
      setOpen(false);
      setBadgeActive(false);
      setMessage(prev => { prev?.onDismiss?.(); return prev; });
    }
  }, []);

  // X button: hard dismiss — clear queue, close, hide badge, keep message for icon recall
  const dismissForNow = useCallback(() => {
    queueRef.current = [];
    setOpen(false);
    setBadgeActive(false);
    setMessage(prev => { prev?.onDismiss?.(); return prev; });
  }, []);

  const clearMessage = useCallback(() => {
    queueRef.current = [];
    setMessage(null);
    setOpen(false);
    setBadgeActive(false);
  }, []);

  const openBubble = useCallback(() => setOpen(true), []);
  const closeBubble = useCallback(() => setOpen(false), []);

  return (
    <CopilotContext.Provider value={{
      message, open, badgeActive,
      showMessage, showMessageSilent, queueMessages, clearMessage,
      openBubble, closeBubble, advanceQueue, acknowledgeMessage, dismissForNow,
    }}>
      {children}
    </CopilotContext.Provider>
  );
};
